import { useLazyQuery, useMutation }                                           from '@apollo/client'
import { ArrowUturnLeftIcon }                                                  from '@heroicons/react/24/solid'
import { useEffect, useState }                                                 from 'react'
import { useDispatch }                                                         from 'react-redux'
import { useNavigate, useParams }                                              from 'react-router-dom'
import { appCdnUrl, formatGBP, formatTimeDate }                                from '../../utilities'
import { Button }                                                              from '../../components'
import { LoadingEllipsis }                                                     from '../../components/Loaders'
import { SelectOption }                                                        from '../../components/Select/Select'
import { StatusPill, statusMap }                                               from '../../components/StatusPill'
import { GET_SHOP_ORDER_BY_ID, UPDATE_SHOP_ORDER, RESEND_CONFIRMATION_EMAILS } from '../../graphql/shop'
import { setToasts, showToaster }                                              from '../../features/toaster/toasterSlice'
import MainLayout                                                              from '../../layouts/MainLayout'

export default function ShopOrder(): JSX.Element {
  const { order_id }                            = useParams()
  const dispatch: any                           = useDispatch()
  const navigate: any                           = useNavigate()
  const [ shopOrder, setShopOrder ]             = useState( null )
  const [ shopOrderStatus, setShopOrderStatus ] = useState( '' )

  const [ getShopOrder, { loading, refetch } ] = useLazyQuery( GET_SHOP_ORDER_BY_ID, {
    onCompleted: async ( { shopOrderById }: any ): Promise<void> => {
      if ( !shopOrderById ) {
        navigate( '/shop/orders' )
        dispatchMessage( 'error', 'Order not found!' )
      } else {
        setShopOrder( shopOrderById )
        setShopOrderStatus( shopOrderById.status )
      }
    },
    onError:     ( error: any ): void => console.log( error )
  } )

  const [ updateShopOrder ] = useMutation( UPDATE_SHOP_ORDER, {
    onCompleted: async (): Promise<void> => {
      await refetch()
      dispatchMessage( 'success', 'Order status updated.' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Update Order failed. Please try again' )
  } )

  const [ resendConfirmationEmails, { loading: sending } ] = useMutation( RESEND_CONFIRMATION_EMAILS, {
    onCompleted: ( { resendConfirmationEmails: { data } } ): void => {
      if ( data === true ) {
        dispatchMessage( 'success', 'Email sent' )
      } else {
        dispatchMessage( 'error', 'Email NOT sent' )
      }
    },
    onError:     ( error: any ): void => {
      dispatchMessage( 'error', 'Email NOT sent' )
      console.log( error )
    }
  } )

  const sendClientConfirmationEmail: any = async (): Promise<any> => {
    const confirmed: boolean = window.confirm( 'Are you sure you want to send confirmation email to CLIENT?' )
    if ( confirmed ) {
      await resendConfirmationEmails( { variables: { input: { id: order_id, type: 'client' } } } )
    }
  }

  const sendSupplierConfirmationEmail: any = async (): Promise<any> => {
    const confirmed: boolean = window.confirm( 'Are you sure you want to send emails to SUPPLIERS?' )
    if ( confirmed ) {
      await resendConfirmationEmails( { variables: { input: { id: order_id, type: 'suppliers' } } } )
    }
  }

  const fetchShopOrder: any = async (): Promise<void> => {
    await getShopOrder( { variables: { input: { id: order_id } } } )
  }

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  const handleStatusUpdate: any = async (): Promise<void> => {
    if ( shopOrder.status !== shopOrderStatus ) {
      await updateShopOrder( { variables: { input: { id: shopOrder.id, status: shopOrderStatus } } } )
    }
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch && !loading ) {
      fetchShopOrder()
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ order_id ] )
  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { loading && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }
          { !loading && shopOrder &&
            <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 lg:p-5">
              <div className="flex w-full mb-4">
                <header className="mb-4">
                  <h1 className="sm:text-2xl text-xl font-bold">Shop Order</h1>
                </header>
              </div>

              <main className="max-w-7xl mx-auto p-6 space-y-6">
                <div className="col flex 2xl:-mb-4">
                  <div className="flex">
                    <Button
                      label="Back"
                      blue
                      onClick={ (): any => navigate( -1 ) }
                      icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
                    />
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-lg font-semibold">Order #{ shopOrder.roi_id }</h1>

                    <div className="text-sm font-medium">ID: { shopOrder.id }</div>

                    <div className="flex items-center gap-2">
                      <StatusPill status={ shopOrder.status } />
                    </div>
                  </div>
                  <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Order Date</div>
                      <div className="mt-1 font-medium">{ formatTimeDate( shopOrder.created_at, true ) }</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">User</div>
                      <div className="mt-1 font-medium">{ shopOrder.user.name }</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Location</div>
                      <div className="mt-1 font-medium">{ shopOrder.delivery_address.name }</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="text-xs text-gray-500">Transaction ID</div>
                      <div className="mt-1 font-mono text-sm overflow-x-auto">{ shopOrder.transaction_id }</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <section className="lg:col-span-2 bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-base font-semibold">Products</h2>
                    </div>

                    <div className="px-6 py-4 space-y-4">
                      { Object.values( shopOrder.basket ).map( ( product: any, index: number ): any =>
                                                                 <article key={ index } className="grid grid-cols-1 gap-4 rounded-xl ring-1 ring-gray-200 p-4 sm:grid-cols-[auto,1fr,auto] sm:items-start">
                                                                   <img
                                                                     src={ appCdnUrl + product.image }
                                                                     alt={ product.name }
                                                                     className="h-16 w-16 rounded-lg object-cover ring-1 ring-gray-200 justify-self-start"
                                                                   />

                                                                   <div>
                                                                     <div className="font-medium text-gray-900">{ product.name }</div>
                                                                     <div className="mt-2 flex flex-wrap gap-2">
                                                                       { product.variants && Object.values( product.variants ).map( ( v: any, i: number ): any => (
                                                                         <span key={ i }>
                                                                           <span className="space-x-1">
                                                                             { Object.keys( v.config )
                                                                                     .filter( ( key: any ): any => key !== 'variation_id' )
                                                                                     .map( ( val: any, it: number ): any =>
                                                                                             <span
                                                                                               key={ it }
                                                                                               className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200"
                                                                                             >
                                                                                               { v.config[ val ] }
                                                                                             </span> )
                                                                             }
                                                                           </span>
                                                                           <span> x{ v.quantity }</span>
                                                                         </span>
                                                                       ) ) }
                                                                     </div>
                                                                   </div>

                                                                   <div className="flex items-center justify-end gap-4 sm:flex-col sm:items-end sm:gap-2 text-sm">
                                                                     <div>
                                                                       <div className="text-xs text-right uppercase tracking-wide text-gray-500">Price</div>
                                                                       <div className="rounded-lg bg-gray-50 px-2.5 py-1 font-semibold ring-1 ring-gray-200">
                                                                         { formatGBP( product.price ) }
                                                                       </div>
                                                                     </div>
                                                                     <div>
                                                                       <div className="text-xs text-right uppercase tracking-wide text-gray-500">VAT</div>
                                                                       <div className="rounded-lg bg-rose-50 px-2.5 py-1 font-semibold text-rose-700 ring-1 ring-rose-200">
                                                                         { product.vat }%
                                                                       </div>
                                                                     </div>
                                                                   </div>
                                                                 </article> )
                      }

                      <hr className="border-gray-200" />

                      <div className="grid grid-cols-1 gap-6">
                        <div className="rounded-xl bg-gray-50 p-4">
                          <dl className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <dt className="text-gray-600">Sub-Total</dt>
                              <dd className="font-semibold text-gray-900">{ formatGBP( shopOrder.subtotal ) }</dd>
                            </div>
                            <div className="flex items-center justify-between">
                              <dt className="text-gray-600">VAT</dt>
                              <dd className="font-semibold text-gray-900">{ formatGBP( shopOrder.total_vat ) }</dd>
                            </div>
                            <div className="flex items-center justify-between">
                              <dt className="text-gray-600">Delivery</dt>
                              <dd className="font-semibold text-gray-900">{ formatGBP( shopOrder.delivery_cost ) }</dd>
                            </div>
                          </dl>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                        <div className="text-base font-semibold">Total:</div>
                        <div className="text-lg font-bold text-gray-900">{ formatGBP( shopOrder.total ) }</div>
                      </div>
                    </div>
                  </section>

                  <aside className="space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-base font-semibold">Delivery Address</h3>
                      </div>
                      <div className="px-6 py-4">
                        <div className="rounded-xl bg-gray-50 p-4 leading-6">
                          <div className="font-semibold">{ shopOrder.delivery_address.name }</div>
                          { shopOrder.delivery_address.address_line1 }<br />
                          { shopOrder.delivery_address.address_line2 && <>{ shopOrder.delivery_address.address_line2 }<br /></> }
                          { shopOrder.delivery_address.address_city }<br />
                          { shopOrder.delivery_address.address_postcode }
                        </div>
                      </div>
                    </div>

                    {/*<div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">*/}
                    {/*  <div className="px-6 py-4 border-b border-gray-200">*/}
                    {/*    <h3 className="text-base font-semibold">Order Status</h3>*/}
                    {/*  </div>*/}
                    {/*  <div className="px-6 py-4 space-y-3">*/}
                    {/*    <label className="text-sm text-gray-700">Status</label>*/}
                    {/*    <div className="flex items-center gap-3">*/}
                    {/*      <SelectOption*/}
                    {/*        wrapperClasses="w-full"*/}
                    {/*        setSelected={ setShopOrderStatus }*/}
                    {/*        selected={ shopOrderStatus }*/}
                    {/*        options={ Object.entries( statusMap ).map( ( [ key, value ]: any ): any => {*/}
                    {/*          return { key: key, value: value[ 0 ].label }*/}
                    {/*        } ) }*/}
                    {/*      />*/}
                    {/*      <Button yellow label="Update" onClick={ handleStatusUpdate } />*/}
                    {/*    </div>*/}

                    {/*    <div className="flex flex-wrap gap-2 pt-2 text-xs">*/}
                    {/*      { ( Object.keys( statusMap ) as ( keyof typeof statusMap )[] ).map( ( k ) => (*/}
                    {/*        <StatusPill key={ k } status={ k } />*/}
                    {/*      ) ) }*/}
                    {/*    </div>*/}
                    {/*  </div>*/}
                    {/*</div>*/}

                    {/*<div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">*/}
                    {/*  <div className="px-6 py-4 border-b border-gray-200">*/}
                    {/*    <h3 className="text-base font-semibold">Send confirmation email to Client</h3>*/}
                    {/*  </div>*/}
                    {/*  <div className="px-6 py-4 space-y-3">*/}
                    {/*    <Button yellow loading={ sending } label="Send email to Client" onClick={ sendClientConfirmationEmail } />*/}
                    {/*  </div>*/}
                    {/*</div>*/}

                    {/*<div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200">*/}
                    {/*  <div className="px-6 py-4 border-b border-gray-200">*/}
                    {/*    <h3 className="text-base font-semibold">Send confirmation email to Suppliers</h3>*/}
                    {/*  </div>*/}
                    {/*  <div className="px-6 py-4 space-y-3">*/}
                    {/*    <Button yellow loading={ sending } label="Send email to Suppliers" onClick={ sendSupplierConfirmationEmail } />*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                  </aside>
                </div>
              </main>
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
